/* eslint-disable */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TextButton, Divider, Text, Headline } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import mapContext from '../../../contexts/map-context';
import type { DealerReview } from '../../../types';
import { CustomerReview } from './CustomerReview';
import { generateMercantileValue } from '../../../utils/generateMercantileValue';
import { ReviewDisclaimer } from '../../common/ReviewDisclaimer/ReviewDisclaimer';
import { messages } from '../../../i18n/messages';

const STAR_FILTER_VALUE: { [key: string]: number } = {
  '1 star': 1,
  '2 star': 2,
  '3 star': 3,
  '4 star': 4,
  '5 star': 5,
};

const CustomerReviewListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubHeader = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
  padding-bottom: 24px;
`;

const TextStyled = styled(Headline)`
  font-size: 18px;
`;

const DividerStyled = styled(Divider)`
  margin: 24px 0;
`;

const NoReviewsText = styled(Text)`
  text-align: center;
`;

export const CustomerReviewList = () => {
  const { dealerReviews, reviewCategoryFilter, reviewStarFilter, reviewSort } =
    mapContext.useMapState();
  const [length, setLength] = useState(5);
  const [filteredReviews, setFilteredReviews] = useState<DealerReview[]>([]);
  const loadMore = () => {
    setLength(length + 5);
  };
  const viewMoreLabel = useI18n(messages.five_star_view_more);
  const reviewLabel = useI18n(messages.five_star_reviews);
  useEffect(() => {
    if (dealerReviews) {
      const dealerReviewCopy = dealerReviews.slice();
      const categoryFilteredReviews =
        reviewCategoryFilter === 'ALL'
          ? dealerReviewCopy
          : dealerReviewCopy.filter((review) => review.categoryKey === reviewCategoryFilter);
      const starFilteredReviews =
        reviewStarFilter === 'All stars'
          ? categoryFilteredReviews
          : categoryFilteredReviews.filter(
              (review) =>
                Math.floor(+generateMercantileValue(review.rating)) ===
                STAR_FILTER_VALUE[reviewStarFilter],
            );
      const sortedReviews = (() => {
        switch (reviewSort) {
          case 'Newest':
            return starFilteredReviews;
          case 'Oldest':
            return starFilteredReviews.reverse();
          case 'Highest':
            return starFilteredReviews.sort((a, b) => {
              if (a.rating !== b.rating) {
                return b.rating - a.rating;
              }
              if (a.date.getTime() > b.date.getTime()) {
                return -1;
              }
              return 1;
            });
          case 'Lowest':
            return starFilteredReviews.sort((a, b) => {
              if (a.rating !== b.rating) {
                return a.rating - b.rating;
              }
              if (a.date.getTime() > b.date.getTime()) {
                return -1;
              }
              return 1;
            });
          default:
            return starFilteredReviews;
        }
      })();
      setFilteredReviews(sortedReviews);
      setLength(5);
    }
  }, [dealerReviews, reviewCategoryFilter, reviewStarFilter, reviewSort]);
  const reviewCount = filteredReviews.length;
  if (reviewCount > 0) {
    const reviewCountText = ` ${reviewLabel}`;
    return (
      <CustomerReviewListContainer>
        <SubHeader>
          <TextStyled tag="span" variant={{ order: '4', type: 'headline', weight: 'bold' }}>
            {reviewCount} {reviewCountText}
          </TextStyled>
          <div>
            <ReviewDisclaimer />
          </div>
        </SubHeader>
        {filteredReviews.slice(0, length).map((review: DealerReview, index) => (
          <React.Fragment key={review.id}>
            <CustomerReview review={review} />
            {index < filteredReviews.length - 1 && <DividerStyled />}
          </React.Fragment>
        ))}
        {length < filteredReviews.length && (
          <TextButton variant="secondary" onClick={loadMore}>
            {viewMoreLabel}
          </TextButton>
        )}
      </CustomerReviewListContainer>
    );
  }
  const noReviewsText = (() => {
    if (reviewCategoryFilter === 'ALL') {
      return useI18n(messages.five_star_no_reviews_all);
    }
    if (reviewCategoryFilter === 'SALES') {
      return useI18n(messages.five_star_no_reviews_sales);
    }
    return useI18n(messages.five_star_no_reviews_service);
  })();
  return (
    <CustomerReviewListContainer>
      <NoReviewsText variant={{ order: '1', style: 'normal', type: 'copy' }}>
        {noReviewsText}
      </NoReviewsText>
    </CustomerReviewListContainer>
  );
};
